import type { Anamnesis } from '../types/anamnesis';
import type { ParQFormData } from '../types/registration';
import type { ParqYesAnswer, ParqYesAnswers } from '../types/lead';

export type ParqBooleanFormField = Exclude<keyof ParQFormData, 'heartConditionOther' | 'responsibility'>;

export type ParqBooleanDbColumn =
  | 'parq_heart_condition'
  | 'parq_chest_pain_activity'
  | 'parq_chest_pain_recent'
  | 'parq_dizziness'
  | 'parq_bone_joint_problem'
  | 'parq_takes_bp_medication'
  | 'parq_other_reason';

export type ParqDetailFormField = 'heartConditionOther';

export interface ParqQuestionDefinition {
  field: ParqBooleanFormField;
  answerField: string;
  dbColumn: ParqBooleanDbColumn;
  detailDbColumn?: 'parq_heart_condition_other';
  detailFormField?: ParqDetailFormField;
  detailLabel?: string;
  detailPlaceholder?: string;
  detailRequiredMessage?: string;
  label: string;
}

export const PARQ_QUESTIONS: ParqQuestionDefinition[] = [
  {
    field: 'heartCondition',
    answerField: 'heart_condition',
    dbColumn: 'parq_heart_condition',
    detailDbColumn: 'parq_heart_condition_other',
    detailFormField: 'heartConditionOther',
    detailLabel: 'Especifique o problema cardíaco',
    detailPlaceholder: 'Descreva...',
    detailRequiredMessage: 'Descreva o problema cardíaco.',
    label:
      'Algum médico já disse que você possui algum problema de coração e que só deveria realizar atividade física supervisionado por profissionais de saúde?',
  },
  {
    field: 'chestPainActivity',
    answerField: 'chest_pain_activity',
    dbColumn: 'parq_chest_pain_activity',
    label: 'Você sente dores no peito quando pratica atividade física?',
  },
  {
    field: 'chestPainRecent',
    answerField: 'chest_pain_recent',
    dbColumn: 'parq_chest_pain_recent',
    label: 'No último mês, você sentiu dores no peito quando praticou atividade física?',
  },
  {
    field: 'dizziness',
    answerField: 'dizziness',
    dbColumn: 'parq_dizziness',
    label: 'Você apresenta desequilíbrio devido à tontura e/ou perda de consciência?',
  },
  {
    field: 'boneJointProblem',
    answerField: 'bone_joint_problem',
    dbColumn: 'parq_bone_joint_problem',
    label:
      'Você possui algum problema ósseo ou articular que poderia ser piorado pela realização de atividade física?',
  },
  {
    field: 'takesBpMedication',
    answerField: 'takes_bp_medication',
    dbColumn: 'parq_takes_bp_medication',
    label:
      'Você toma atualmente algum medicamento para pressão arterial e/ou problema de coração?',
  },
  {
    field: 'otherReason',
    answerField: 'other_reason',
    dbColumn: 'parq_other_reason',
    label: 'Sabe de alguma outra razão pela qual você não deve praticar atividade física?',
  },
];

export const PARQ_QUESTIONS_BY_ANSWER_FIELD = Object.fromEntries(
  PARQ_QUESTIONS.map((question) => [question.answerField, question]),
) as Record<string, ParqQuestionDefinition>;

export const PARQ_QUESTIONS_BY_FORM_FIELD = Object.fromEntries(
  PARQ_QUESTIONS.map((question) => [question.field, question]),
) as Record<ParqBooleanFormField, ParqQuestionDefinition>;

function normalizeDetail(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function buildParqYesAnswersFromAnamnesis(
  anamnesis: Partial<Anamnesis> | null | undefined,
): ParqYesAnswers {
  if (!anamnesis) {
    return { has_yes_answers: false, answers: [] };
  }

  const answers = PARQ_QUESTIONS.reduce<ParqYesAnswer[]>((items, question) => {
    if (anamnesis[question.dbColumn] !== true) {
      return items;
    }

    const detail = question.detailDbColumn
      ? normalizeDetail(anamnesis[question.detailDbColumn])
      : null;

    items.push({
      field: question.answerField,
      label: question.label,
      detail,
    });

    return items;
  }, []);

  return {
    has_yes_answers: answers.length > 0,
    answers,
  };
}

export function getParqAnswerLabel(answer: Pick<ParqYesAnswer, 'field' | 'label'>): string {
  return PARQ_QUESTIONS_BY_ANSWER_FIELD[answer.field]?.label ?? answer.label;
}
